import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, Scale } from 'lucide-react';

import Button from '../../../components/ui/Button';
import TextInput from '../../../components/ui/TextInput';
import { formatDate } from '../../../utils/Utils';

// P1-C — sonuç itirazı satırı + modalı. API kontratı B paketinin plan'da
// SABİTLENMİŞ sözleşmesi (POST v1/objections/my {periodId, note}); pencere
// durumu BE'nin objectionWindowEndsAt alanından türetilir: null → pencere
// hesaplanamıyor (eski dönem) → buton pasif; geçmişse → pasif + "kapandı"
// etiketi; açıksa → aktif + kalan gün etiketi. Kalibrasyon el sıkışmasındaki
// itiraz notundan (MyCalibrationPage) FARKLI bir süreçtir — başlık "Sonuç
// İtirazı" olarak ayrışır.
const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 14px 20px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const IconWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const Body = styled.div`
  flex: 1;
  min-width: 200px;
`;

const CardTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardMeta = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 2px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ $status }) => ($status === 'accepted' ? '#DCFCE7' : $status === 'rejected' ? '#FEF2F2' : '#FFF7ED')};
  color: ${({ $status }) => ($status === 'accepted' ? '#16A34A' : $status === 'rejected' ? '#E53935' : '#EA580C')};
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 6px;
  width: 480px;
  max-width: 92vw;
  padding: 24px;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: #a0a8b4;
  &:hover { background: #f1f5f9; color: #004242; }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

function remainingDays(endsAt) {
  const end = new Date(endsAt);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const nowDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  // Pencere son günü DAHİL (BE iş günü hesabı gün döner; gün sonuna kadar açık).
  return Math.floor((endDay - nowDay) / 86400000) + 1;
}

export default function ObjectionCard({ objectionWindowEndsAt, objection, onSubmit }) {
  const { t, i18n } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const daysLeft = useMemo(
    () => (objectionWindowEndsAt ? remainingDays(objectionWindowEndsAt) : null),
    [objectionWindowEndsAt],
  );
  const windowOpen = daysLeft != null && daysLeft > 0;

  const handleSubmit = useCallback(async () => {
    if (!note.trim()) return;
    setSaving(true);
    const ok = await onSubmit(note.trim());
    setSaving(false);
    if (ok) {
      setModalOpen(false);
      setNote('');
    }
  }, [note, onSubmit]);

  return (
    <Card>
      <IconWrap><Scale size={20} /></IconWrap>
      <Body>
        <CardTitle>{t('performance.results.objectionTitle')}</CardTitle>
        <CardMeta>
          {objection
            ? `${t('performance.results.objectionSubmitted')}${objection.decisionNote ? ` — ${objection.decisionNote}` : ''}`
            : windowOpen
              ? t('performance.results.objectionDaysLeft', { count: daysLeft, date: formatDate(objectionWindowEndsAt, i18n.language) })
              : t('performance.results.objectionWindowClosed')}
        </CardMeta>
      </Body>
      {objection ? (
        <StatusBadge $status={objection.status}>
          {t(`performance.results.objectionStatus.${objection.status}`, { defaultValue: objection.status })}
        </StatusBadge>
      ) : (
        <Button variant="outline" disabled={!windowOpen} onClick={() => setModalOpen(true)}>
          {t('performance.results.objectionButton')}
        </Button>
      )}

      {modalOpen && (
        <Overlay onClick={() => !saving && setModalOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{t('performance.results.objectionModalTitle')}</ModalTitle>
              <CloseBtn onClick={() => !saving && setModalOpen(false)}><X size={18} /></CloseBtn>
            </ModalHeader>
            <TextInput
              type="textarea"
              rows={4}
              label={t('performance.results.objectionNote')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              required
            />
            <ModalFooter>
              <Button variant="ghost" onClick={() => setModalOpen(false)} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSubmit} disabled={saving || !note.trim()}>
                {saving ? t('common.saving') : t('performance.results.objectionSubmit')}
              </Button>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
    </Card>
  );
}
